import {
  engineEditsMessages,
  isChatStatusBusy,
  type Message,
  useStores,
} from '@littlebird/sdk';
import { useState } from 'react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import { chatEngineStore } from '@/features/chat/chat-engine.store';
import {
  localChatEventsBus,
  USER_DID_SEND_MESSAGE_EVENT,
} from '@/features/chat/chat.events';
import { resolveChatMode } from '@/features/chat/chat.hooks';
import { teamChatPostOf } from '@/features/team-chats/team-chat-messages';
import { teamChatsEnabled } from '@/features/team-chats/use-team-chat-config';
import { trackEvent } from '@/utils/analytics';

const hasTeamPostAfter = (
  messages: readonly Message[],
  messageId: string
): boolean => {
  const index = messages.findIndex((each) => each.id === messageId);
  return (
    index !== -1 &&
    messages.slice(index + 1).some((each) => teamChatPostOf(each) !== null)
  );
};

export const useUserMessageEdit = (
  threadId: string,
  message: Message,
  allowEdit: boolean
) => {
  const { threads: threadsStore, messages: messagesStore } = useStores();
  const [isEditing, setIsEditing] = useState(false);
  const [isSwitchingVersion, setIsSwitchingVersion] = useState(false);
  const thread = threadsStore.getById(threadId);
  const isThreadBusy = isChatStatusBusy(thread?.status);
  // An edit would move teammates' later posts off the shown branch, so the server refuses it
  const lockedByTeamPosts =
    teamChatsEnabled() &&
    hasTeamPostAfter(messagesStore.getMessagesByThreadId(threadId), message.id);
  const canEdit =
    allowEdit &&
    !lockedByTeamPosts &&
    Boolean(thread?.synced) &&
    !isThreadBusy &&
    !message.askUserAnswers &&
    !message.replacesMessageId &&
    engineEditsMessages(chatEngineStore.engineByChat[threadId]);

  const submitEdit = (body: string) => {
    setIsEditing(false);
    const userMessageId = uuidv4();
    messagesStore
      .editMessage({
        threadId,
        messageId: message.id,
        body,
        mode: resolveChatMode(threadId),
        useTodos: false,
        userMessageId,
      })
      .catch((error: unknown) =>
        toast.error(
          error instanceof Error
            ? error.message
            : "Couldn't edit this message. Try again."
        )
      );
    if (!messagesStore.get(userMessageId)) return;
    localChatEventsBus.emit(USER_DID_SEND_MESSAGE_EVENT, {
      data: { threadId, messageId: userMessageId },
    });
    trackEvent('MESSAGE_USER_EDITED', { chatId: threadId });
  };

  const showVersion = async (versionId: string) => {
    setIsSwitchingVersion(true);
    try {
      await messagesStore.activateMessageVersion(threadId, versionId);
      trackEvent('MESSAGE_USER_VERSION_SWITCHED', { chatId: threadId });
    } catch {
      toast.error("Couldn't switch to that version. Try again.");
    } finally {
      setIsSwitchingVersion(false);
    }
  };

  return {
    canEdit,
    lockedByTeamPosts,
    isEditing,
    startEditing: () => setIsEditing(true),
    cancelEditing: () => setIsEditing(false),
    submitEdit,
    canSwitchVersion: allowEdit && !isThreadBusy && !isSwitchingVersion,
    showVersion,
  };
};
